import React, { useMemo, type MutableRefObject } from 'react';
import type { FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { UI_OPERATION_STATUS_REQUEST } from '@uncinc/react-drupal-core';

import {
  FormikWrapper,
  Tag,
  FormTextarea as Textarea,
} from '@uncinc/uncinc-react-kitchen-sink';

import useClasses from '../../../../hooks/useClasses';
import useOperationStatus from '../../../../hooks/useOperationStatus';
import Button from '../../../Button';

import './style.scss';

export interface FormForumCommentValues {
  body: string;
  parentCommentId?: number;
  parentAuthorName?: string;
}

export type FormForumCommentHelpers = FormikHelpers<FormForumCommentValues>;

interface Props {
  postTitle?: string;
  label?: string;
  initialValues: FormForumCommentValues;
  scrollRef?: MutableRefObject<HTMLDivElement>;
  className?: string;
  onSubmit: (values: FormForumCommentValues, formProps) => void;
  onDeleteMention?: () => void;
  onCancel?: () => void;
}

const ForumComment: React.FC<Props> = ({
  postTitle,
  label,
  initialValues,
  scrollRef,
  className,
  onSubmit,
  onDeleteMention,
  onCancel,
}) => {
  const { t } = useTranslation();

  const { joinClasses, buildClass } = useClasses('ForumComment');
  const operationStatusAdd = useOperationStatus('comment', 'add');
  const operationStatusEdit = useOperationStatus('comment', 'edit');
  const disableSubmitButton = useMemo(() => (
    operationStatusAdd === UI_OPERATION_STATUS_REQUEST ||
    operationStatusEdit === UI_OPERATION_STATUS_REQUEST
  ), [operationStatusAdd, operationStatusEdit]);

  const validationSchema = Yup.object().shape({
    body: Yup.string()
      .trim()
      .required(t('pages.forum.comment_form.fields.body.validation.required')),
  });

  return (
    <div className={joinClasses(buildClass(), className)}>
      <FormikWrapper
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        validateOnBlur={false}
        validateOnChange={false}
        onSubmit={(values: FormForumCommentValues, formProps: FormForumCommentHelpers) => {
          const { setSubmitting } = formProps;
          onSubmit(values, formProps);
          setSubmitting(false);
        }}
      >
        {postTitle && (
          <h2 className="asH4">
            {t('pages.forum.comment_form.title', { title: postTitle })}
          </h2>
        )}

        <div ref={scrollRef} className={buildClass('textarea')}>
          <label htmlFor="body" className={buildClass('textarea__label')}>
            {label || t('pages.forum.comment_form.fields.body.label')}
          </label>

          <div className={buildClass('textarea__inner')}>
            {(initialValues.parentAuthorName && onDeleteMention) && (
              <div className={buildClass('mention-wrapper')}>
                <Tag
                  className={buildClass('mention')}
                  name={`@${initialValues.parentAuthorName}`}
                  stretchClickableArea
                  deleteButtonAriaLabel={t('aria-labels.delete_forum_mention', { name: initialValues.parentAuthorName })}
                  onDelete={onDeleteMention}
                />
              </div>
            )}

            <Textarea
              fieldName="body"
              value={initialValues.body}
              placeholder={t('pages.forum.comment_form.fields.body.placeholder')}
            />
          </div>
        </div>

        <div className={buildClass('buttons')}>
          <Button htmlType="submit" buttonState={disableSubmitButton && 'disabled'}>
            {t('pages.forum.comment_form.submit')}
          </Button>

          {onCancel && (
            <Button asLink onClick={onCancel}>
              {t('buttons.cancel')}
            </Button>
          )}
        </div>
      </FormikWrapper>
    </div>
  );
};

export default ForumComment;
